import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useModalEscape } from '../lib/useModalEscape';
import { StatCard } from '../components/Icon';
import { fmtBooking } from '../lib/dates';

type Status = 'upcoming' | 'past' | 'cancelled';
type Attendance = 'attended' | 'missed' | null;
interface Attendee { name?: string; email?: string }
interface Booking {
  id: string | number; uid?: string; title?: string; description?: string;
  attendees?: Attendee[];
  name?: string; email?: string; attendee?: string;
  start?: string; startTime?: string; start_time?: string;
  duration?: number; status?: string;
  meetingUrl?: string; meeting_url?: string; location?: string;
  attendance?: Attendance;
}
interface BookingsResponse { data?: Booking[]; pagination?: { totalItems?: number } }

function statusStyle(status?: string): { className: string; style?: CSSProperties } {
  switch (status) {
    case 'accepted': return { className: 'badge badge-green' };
    case 'pending': return { className: 'badge badge-amber' };
    case 'cancelled': return { className: 'badge', style: { background: 'rgba(239,68,68,0.15)', color: '#EF4444' } };
    default: return { className: 'badge badge-gray' };
  }
}
const bookingWhen = (b: Booking) => b.start || b.startTime || b.start_time;
const bookingLink = (b: Booking) => b.meetingUrl || b.meeting_url || (b.location && /^https?:\/\//.test(b.location) ? b.location : undefined);
const bookingAttendees = (b: Booking): Attendee[] => (b.attendees && b.attendees.length)
  ? b.attendees : (b.name || b.email || b.attendee) ? [{ name: b.attendee || b.name, email: b.email }] : [];
const bookingEmail = (b: Booking) => bookingAttendees(b)[0]?.email || b.email || '—';

function AttendanceTag({ a }: { a: Attendance }) {
  if (a === 'attended') return <span className="badge badge-green">attended</span>;
  if (a === 'missed') return <span className="badge" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>missed</span>;
  return <span className="muted small">—</span>;
}

export default function Bookings() {
  const { can } = useAuth();
  const canWrite = can('bookings', 'write');
  const [status, setStatus] = useState<Status>('upcoming');
  const [view, setView] = useState<'latest' | 'grouped'>('latest');
  const [rows, setRows] = useState<Booking[]>([]);
  const [counts, setCounts] = useState({ upcoming: 0, past: 0, cancelled: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [openEmail, setOpenEmail] = useState<string | null>(null);

  const loadCounts = useCallback(() => {
    Promise.all((['upcoming', 'past', 'cancelled'] as Status[]).map((s) =>
      backendApi.get<BookingsResponse>(`/license/admin/bookings?status=${s}&take=1`)
        .then((d) => d.pagination?.totalItems ?? 0).catch(() => 0)))
      .then(([upcoming, past, cancelled]) => setCounts({ upcoming, past, cancelled }));
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const loadRows = useCallback(() => {
    setLoading(true);
    backendApi.get<BookingsResponse>(`/license/admin/bookings?status=${status}&take=100`)
      .then((d) => { setRows(d.data || []); setError(''); })
      .catch((err: ApiError) => setError(
        err.isForbidden ? 'You do not have access to bookings.'
          : /cal\.com/i.test(err.message) ? 'Bookings need a Cal.com API key, which is not configured on this tier.'
          : err.message))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { loadRows(); }, [loadRows]);

  function refresh() { loadRows(); loadCounts(); }
  // Update one row's attendance in place (avoids a full reload).
  const applyAttendance = (uid: string, a: Attendance) =>
    setRows((rs) => rs.map((b) => (b.uid === uid ? { ...b, attendance: a } : b)));

  const attended = rows.filter((b) => b.attendance === 'attended').length;
  const missed = rows.filter((b) => b.attendance === 'missed').length;

  const groups = useMemo(() => {
    const m = new Map<string, Booking[]>();
    rows.forEach((b) => { const k = bookingEmail(b); if (!m.has(k)) m.set(k, []); m.get(k)!.push(b); });
    return [...m.entries()].map(([key, bs]) => ({
      key,
      total: bs.length,
      attended: bs.filter((b) => b.attendance === 'attended').length,
      missed: bs.filter((b) => b.attendance === 'missed').length,
    })).sort((a, b) => b.total - a.total);
  }, [rows]);
  const gNeedle = q.trim().toLowerCase();
  const groupsFiltered = groups.filter((g) => !gNeedle || g.key.toLowerCase().includes(gNeedle));
  const emailRows = openEmail ? rows.filter((b) => bookingEmail(b) === openEmail) : [];
  const openBooking = openId ? rows.find((b) => String(b.id) === openId) || null : null;

  return (
    <>
      <header className="page-head">
        <div><h1>Bookings</h1><p className="muted">Scheduled calls and demos.</p></div>
        <button className="btn" onClick={refresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </header>

      <div className="stat-row">
        <StatCard icon="calendar" tint="purple" value={counts.upcoming} label="Upcoming" />
        <StatCard icon="check" tint="green" value={counts.past} label="Completed" />
        <StatCard icon="alert" tint="amber" value={counts.cancelled} label="Cancelled" />
      </div>

      <div className="card">
        <div className="filter-bar" style={{ justifyContent: 'space-between' }}>
          <div className="seg">
            {(['upcoming', 'past', 'cancelled'] as Status[]).map((s) => (
              <button key={s} className={'seg-btn' + (status === s ? ' on' : '')} onClick={() => setStatus(s)}>{s}</button>
            ))}
          </div>
          <div className="seg">
            <button className={'seg-btn' + (view === 'latest' ? ' on' : '')} onClick={() => setView('latest')}>Latest</button>
            <button className={'seg-btn' + (view === 'grouped' ? ' on' : '')} onClick={() => setView('grouped')}>By attendee</button>
          </div>
        </div>
        {view === 'grouped' && (
          <div className="filter-bar" style={{ marginTop: 10 }}>
            <input type="search" placeholder="Search attendee email…" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: '1 1 240px' }} />
          </div>
        )}
        {(attended > 0 || missed > 0) && (
          <p className="muted small" style={{ margin: '10px 0 0' }}>In this list: {attended} attended · {missed} missed.</p>
        )}
        {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}
      </div>

      {view === 'latest' && (
        <div className="card">
          {loading ? <p className="muted">Loading…</p> : rows.length === 0 ? <p className="muted">No {status} bookings.</p> : (
            <BookingsTable rows={rows} status={status} onOpen={(b) => setOpenId(String(b.id))} />
          )}
        </div>
      )}

      {view === 'grouped' && (
        <div className="card">
          <h2 className="card-title">By Attendee</h2>
          {loading ? <p className="muted">Loading…</p> : groupsFiltered.length === 0 ? <p className="muted">No attendees match.</p> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Attendee</th><th>Meetings</th><th>Attended</th><th>Missed</th></tr></thead>
                <tbody>
                  {groupsFiltered.map((g) => (
                    <tr key={g.key} className="clickable-row" onClick={() => setOpenEmail(g.key)}>
                      <td>{g.key}</td>
                      <td className="mono">{g.total}</td>
                      <td className="mono">{g.attended || '—'}</td>
                      <td className="mono">{g.missed || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {openEmail && (
        <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); setOpenEmail(null); }}>
          <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2 className="card-title" style={{ margin: 0 }}>{openEmail} — {emailRows.length} meeting{emailRows.length === 1 ? '' : 's'}</h2>
              <button className="btn-icon conv-modal-close" aria-label="Close" onClick={() => setOpenEmail(null)}>×</button>
            </div>
            <div className="account-modal-body">
              <BookingsTable rows={emailRows} status={status} onOpen={(b) => setOpenId(String(b.id))} hideAttendee />
            </div>
          </div>
        </div>
      )}

      {openBooking && (
        <MeetingModal booking={openBooking} canWrite={canWrite}
          onAttendance={applyAttendance} onClose={() => setOpenId(null)} />
      )}
    </>
  );
}

function BookingsTable({ rows, status, onOpen, hideAttendee }:
  { rows: Booking[]; status: Status; onOpen: (b: Booking) => void; hideAttendee?: boolean }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr>
          <th>Title</th>{!hideAttendee && <th>Attendees</th>}<th>Date &amp; Time</th><th>Duration</th><th>Status</th>
          {status === 'past' && <th>Attendance</th>}<th>Meeting Link</th>
        </tr></thead>
        <tbody>
          {rows.map((b) => {
            const attendees = bookingAttendees(b);
            const sb = statusStyle(b.status);
            const link = bookingLink(b);
            const w = fmtBooking(bookingWhen(b));
            return (
              <tr key={String(b.id)} className="clickable-row" onClick={() => onOpen(b)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{b.title || '—'}</div>
                  {b.description && (
                    <div className="muted small" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.description}</div>
                  )}
                </td>
                {!hideAttendee && (
                  <td>{attendees.length === 0 ? <span className="muted">—</span> : attendees.map((a, i) => (
                    <div key={i}>{a.name || a.email || '—'}{a.name && a.email && <span className="muted small block">{a.email}</span>}</div>
                  ))}</td>
                )}
                <td>{w.time ? <><div style={{ whiteSpace: 'nowrap' }}>{w.date}</div><div className="muted small">{w.time}</div></> : <span className="muted">—</span>}</td>
                <td className="mono">{b.duration ? `${b.duration}m` : '—'}</td>
                <td><span className={sb.className} style={sb.style}>{b.status || status}</span></td>
                {status === 'past' && <td><AttendanceTag a={b.attendance ?? null} /></td>}
                <td onClick={(e) => e.stopPropagation()}>{link ? <a className="link-pill" href={link} target="_blank" rel="noreferrer">Join</a> : <span className="muted">—</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MeetingModal({ booking, canWrite, onAttendance, onClose }:
  { booking: Booking; canWrite: boolean; onAttendance: (uid: string, a: Attendance) => void; onClose: () => void }) {
  useModalEscape(onClose);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [att, setAtt] = useState<Attendance>(booking.attendance ?? null);
  const attendees = bookingAttendees(booking);
  const link = bookingLink(booking);
  const w = fmtBooking(bookingWhen(booking));

  async function set(a: Attendance) {
    if (!booking.uid) { setErr('This booking has no id to tag.'); return; }
    setBusy(true); setErr('');
    try {
      await backendApi.put(`/license/admin/bookings/${booking.uid}/attendance`, { status: a });
      setAtt(a); onAttendance(booking.uid, a);
    } catch (e: any) { setErr(e.body?.error || e.message); } finally { setBusy(false); }
  }

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{booking.title || 'Meeting'}</h2>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          <dl className="detail-grid">
            <dt>When</dt><dd>{w.time ? `${w.date} · ${w.time}` : '—'}</dd>
            <dt>Duration</dt><dd>{booking.duration ? `${booking.duration} min` : '—'}</dd>
            <dt>Status</dt><dd>{booking.status || '—'}</dd>
            <dt>Attendance</dt><dd><AttendanceTag a={att} /></dd>
            <dt>Meeting link</dt><dd>{link ? <a className="link" href={link} target="_blank" rel="noreferrer">{link}</a> : '—'}</dd>
          </dl>

          <div className="ad-section-title">Attendees</div>
          {attendees.length === 0 ? <p className="muted small">—</p> : (
            <ul className="plain-list">{attendees.map((a, i) => (
              <li key={i}>{a.name || '—'}{a.email && <span className="muted small"> · {a.email}</span>}</li>
            ))}</ul>
          )}

          {booking.description && (<>
            <div className="ad-section-title">Details</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>{booking.description}</div>
          </>)}

          {canWrite && (<>
            <div className="ad-section-title">Set attendance</div>
            {err && <div className="alert alert-error">{err}</div>}
            <div className="row-actions">
              <button className={'btn btn-sm' + (att === 'attended' ? ' btn-primary' : '')} disabled={busy} onClick={() => set('attended')}>Attended</button>
              <button className={'btn btn-sm' + (att === 'missed' ? ' btn-danger' : '')} disabled={busy} onClick={() => set('missed')}>Missed</button>
              {att && <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => set(null)}>Clear</button>}
            </div>
          </>)}
        </div>
      </div>
    </div>, document.body);
}
